/**
 * UKC Weight Pull weight classes, per the official UKC Weight Pull Rulebook.
 * Single source of truth for the fixed weight bands and for automatically
 * determining a dog's weight class from its recorded weight (lbs).
 *
 * Usage:
 *   import { WeightPullWeightClasses, getWeightPullWeightClass } from "@eagerdog/constants";
 *   getWeightPullWeightClass(47) // → "50 lbs (41 – 50 lbs)"
 */
export interface IWeightPullWeightClass {
  /** Display label / stored value for Score.weight_pull.weight_class */
  label: string;
  /** Inclusive upper bound in lbs for this band; undefined = unlimited (top band) */
  maxLbs?: number;
}

export const WeightPullWeightClasses: IWeightPullWeightClass[] = [
  { label: "10 lbs (Under 11 lbs)", maxLbs: 10 },
  { label: "20 lbs (11 – 20 lbs)", maxLbs: 20 },
  { label: "30 lbs (21 – 30 lbs)", maxLbs: 30 },
  { label: "40 lbs (31 – 40 lbs)", maxLbs: 40 },
  { label: "50 lbs (41 – 50 lbs)", maxLbs: 50 },
  { label: "60 lbs (51 – 60 lbs)", maxLbs: 60 },
  { label: "70 lbs (61 – 70 lbs)", maxLbs: 70 },
  { label: "80 lbs (71 – 80 lbs)", maxLbs: 80 },
  { label: "90 lbs (81 – 90 lbs)", maxLbs: 90 },
  { label: "100 lbs (91 – 100 lbs)", maxLbs: 100 },
  { label: "Unlimited (101 lbs and over)" }, // no maxLbs = catch-all top band
];

/**
 * Returns the UKC weight class label for a given dog weight (lbs).
 * Returns undefined for non-positive/invalid weights.
 */
export function getWeightPullWeightClass(weightLbs?: number): string | undefined {
  if (weightLbs === undefined || weightLbs === null || !(weightLbs > 0)) {
    return undefined;
  }

  const band = WeightPullWeightClasses.find(
    (b) => b.maxLbs === undefined || weightLbs <= b.maxLbs
  );

  return band?.label;
}

/**
 * Returns just the short class name/number for a weight class label
 * (e.g. "50 lbs (41 – 50 lbs)" → "50", "Unlimited (101 lbs and over)" → "Unlimited"),
 * without the parenthetical range. Returns an empty string if not provided.
 */
export function getWeightPullWeightClassNumber(weightClassLabel?: string): string {
  if (!weightClassLabel) {
    return "";
  }
  return weightClassLabel.split(" ")[0];
}

function normalizeWeightPullWeightClass(label?: string): string {
  if (!label) {
    return "";
  }
  return label
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Finds a saved weight-class setup, allowing dash/spacing differences. */
export function findWeightPullClassSetup<T extends { weight_class?: string }>(
  setups: T[] | undefined,
  weightClass: string
): T | undefined {
  if (!setups?.length || !weightClass) {
    return undefined;
  }

  const exact = setups.find((setup) => setup.weight_class === weightClass);
  if (exact) {
    return exact;
  }

  const normalized = normalizeWeightPullWeightClass(weightClass);
  const byNormalized = setups.find(
    (setup) => normalizeWeightPullWeightClass(setup.weight_class) === normalized
  );
  if (byNormalized) {
    return byNormalized;
  }

  const classNumber = getWeightPullWeightClassNumber(weightClass);
  if (!classNumber) {
    return undefined;
  }
  return setups.find(
    (setup) => getWeightPullWeightClassNumber(setup.weight_class) === classNumber
  );
}

/** Number of increment columns on the UKC Weight Pull judge book (FO54FBL3.2). */
export const WEIGHT_PULL_JUDGE_BOOK_INCREMENT_COLUMNS = 20;

export interface IWeightPullIncrement {
  blocks: number;
  total_weight: number;
}

/**
 * Builds the judge-book increment sequence for a weight class.
 * Column 1 starts at 0 extra blocks (cart / starting weight only);
 * each later column adds `block_drop` more blocks.
 * Returns an empty array when the setup is incomplete.
 */
export function buildWeightPullIncrements(
  setup?: {
    starting_weight?: number;
    block_weight?: number;
    block_drop?: number;
  },
  columnCount: number = WEIGHT_PULL_JUDGE_BOOK_INCREMENT_COLUMNS
): IWeightPullIncrement[] {
  if (!setup) {
    return [];
  }

  const cart = setup.starting_weight;
  const blockWeight = setup.block_weight;
  const drop = setup.block_drop;

  if (
    cart === undefined ||
    cart === null ||
    !(cart >= 0) ||
    blockWeight === undefined ||
    blockWeight === null ||
    !(blockWeight >= 0) ||
    drop === undefined ||
    drop === null ||
    drop < 0 ||
    !Number.isInteger(drop)
  ) {
    return [];
  }

  const increments: IWeightPullIncrement[] = [];
  for (let i = 0; i < columnCount; i++) {
    const blocks = i * drop;
    increments.push({
      blocks,
      total_weight: cart + blocks * blockWeight,
    });
  }
  return increments;
}
